// Buscador del encabezado (snippet anubis-search-bar). El snippet se dibuja
// dos veces, en escritorio y en celular, y cada vez enlaza este archivo: el
// navegador lo baja una sola vez, pero lo ejecutaria dos veces. La marca
// window.__anubisSearchBar evita que cada buscador quede con dos juegos de
// oyentes (dos busquedas por tecla).
(function () {
  'use strict';

  if (window.__anubisSearchBar) return;
  window.__anubisSearchBar = true;

  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 220;
  const MAX_RESULTS = 4;

  // Zero-decimal currencies (CLP, JPY, KRW…) — Shopify Predictive Search API
  // returns the price as the actual amount (no ×100 multiplier).
  // Currencies with decimals (USD, EUR…) return cents (÷100 needed).
  const ZERO_DECIMAL = new Set([
    'BIF','CLP','DJF','GNF','ISK','JPY','KMF','KRW','MGA','PYG',
    'RWF','UGX','UYI','VND','VUV','XAF','XOF','XPF'
  ]);

  function money(amount) {
    if (amount == null || amount === '') return '';
    const currency = window.AnubisShopCurrency || 'CLP';
    const isZeroDecimal = ZERO_DECIMAL.has(currency);
    const value = isZeroDecimal ? Math.round(amount) : amount / 100;
    try {
      // Mismo formato que el resto del sitio: el de la tienda, no el del idioma.
      const f = (window.AnubisShop && window.AnubisShop.moneyFormat) || '${{amount_no_decimals}}';
      const token = (f.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
      let miles = ',', dec = '.';
      if (token.indexOf('comma_separator') !== -1) { miles = '.'; dec = ','; }
      else if (token.indexOf('apostrophe_separator') !== -1) { miles = "'"; }
      else if (token.indexOf('space_separator') !== -1) { miles = ' '; dec = ','; }
      const t = token.indexOf('no_decimals') !== -1 ? String(Math.round(value)) : Number(value).toFixed(2);
      const parts = t.split('.');
      const ent = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, miles);
      return f.replace(/\{\{\s*\w+\s*\}\}/, parts[1] ? ent + dec + parts[1] : ent);
    } catch {
      return (isZeroDecimal ? '$' : '$') + Number(value).toLocaleString('es-CL');
    }
  }

  // Lo que escribe el cliente va a innerHTML: se escapa. Sin esto, un enlace
  // como /search?q=<img onerror=...> ejecutaba codigo al enfocar el buscador.
  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  function highlight(text, query) {
    if (!query) return text;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text.replace(new RegExp('(' + escaped + ')', 'gi'), '<mark>$1</mark>');
  }

  // ── Variant fetcher ────────────────────────────────────────
  const variantCache = new Map();

  async function fetchProductVariants(handle) {
    if (variantCache.has(handle)) return variantCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();

      // Detect the "platform" option index (first non-trivial option)
      const opts = data.options || [];
      const platformIdx = (() => {
        const skip = ['title', 'default title'];
        const i = opts.findIndex(o => !skip.includes((o.name || o).toLowerCase()));
        return i >= 0 ? i : 0;
      })();

      // Return enriched variant list.
      // NOTE: /products/{handle}.js always returns prices in cents (÷100 needed),
      // unlike /search/suggest.json which returns the actual amount for zero-decimal
      // currencies like CLP.
      const variants = (data.variants || []).map(v => ({
        id:              v.id,
        label:           [v.option1, v.option2, v.option3][platformIdx] || v.title,
        price:           Math.round(parseInt(v.price, 10) / 100),
        compareAtPrice:  v.compare_at_price ? Math.round(parseInt(v.compare_at_price, 10) / 100) : 0,
        available:       v.available,
        featuredImage:   v.featured_image?.src || data.featured_image || null,
        url:             `/products/${handle}`,
      }));

      // Collapse identical labels (keep first occurrence)
      const seen = new Set();
      const unique = variants.filter(v => {
        if (seen.has(v.label)) return false;
        seen.add(v.label);
        return true;
      });

      variantCache.set(handle, { variants: unique, isDefaultVariant: data.has_only_default_variant });
      return variantCache.get(handle);
    } catch { return null; }
  }

  // ── Card builder ────────────────────────────────────────────
  function priceHtml(priceInt, compareAt) {
    if (!priceInt) return '';
    const isOnSale = compareAt > 0 && compareAt > priceInt;
    if (isOnSale) {
      const pct = Math.round((1 - priceInt / compareAt) * 100);
      return `<p class="anubis-sd__price anubis-sd__price--sale">
        <span class="anubis-sd__badge">${pct}% OFF</span>
        <del class="anubis-sd__regular">${money(compareAt)}</del>
        <strong class="anubis-sd__sale">${money(priceInt)}</strong>
      </p>`;
    }
    return `<p class="anubis-sd__price"><strong>${money(priceInt)}</strong></p>`;
  }

  function buildVariantCard(opts) {
    const { href, imgSrc, title, label, priceInt, compareAt, available, query } = opts;
    const img = imgSrc
      ? `<img src="${imgSrc}" alt="${title}" width="52" height="52" loading="lazy">`
      : `<span class="anubis-sd__img-placeholder" aria-hidden="true">🎮</span>`;
    const unavail = available === false
      ? '<span class="anubis-sd__soldout">Sin stock</span>' : '';
    const platformBadge = label
      ? `<span class="anubis-sd__platform-badge">${label}</span>` : '';

    const el = document.createElement('a');
    el.className = 'anubis-sd__item' + (available === false ? ' anubis-sd__item--unavail' : '');
    el.href = href;
    el.setAttribute('role', 'option');
    el.setAttribute('data-sd-item', '');
    el.innerHTML = `
      <span class="anubis-sd__thumb">${img}</span>
      <span class="anubis-sd__info">
        <span class="anubis-sd__title">${highlight(title, query)}</span>
        ${platformBadge}
        ${priceHtml(priceInt, compareAt)}
        ${unavail}
      </span>`;
    return el;
  }

  // ── Expand a single suggest product into per-variant cards ──
  async function expandProduct(p, query) {
    const handle = (p.url || '').split('/products/')[1]?.split('?')[0] || '';
    if (!handle) return [buildFallbackCard(p, query)];

    const result = await fetchProductVariants(handle);
    if (!result || result.isDefaultVariant || result.variants.length <= 1) {
      // Single variant or fetch failed → show one card using suggest data
      return [buildFallbackCard(p, query)];
    }

    // One card per variant
    return result.variants.map(v => buildVariantCard({
      href:       v.url,
      imgSrc:     v.featuredImage,
      title:      p.title,
      label:      v.label,
      priceInt:   v.price,
      compareAt:  v.compareAtPrice,
      available:  v.available,
      query,
    }));
  }

  function buildFallbackCard(p, query) {
    const compareAt = parseInt(p.compare_at_price_min || p.compare_at_price || 0, 10);
    return buildVariantCard({
      href:      p.url,
      imgSrc:    p.image,
      title:     p.title,
      label:     null,
      priceInt:  parseInt(p.price, 10),
      compareAt,
      available: p.available !== false,
      query,
    });
  }

  function initBar(wrap) {
    // El editor de temas vuelve a dibujar el encabezado: solo se prepara el
    // buscador que aun no tiene oyentes.
    if (wrap.dataset.searchReady === 'true') return;
    wrap.dataset.searchReady = 'true';

    const input    = wrap.querySelector('[data-search-input]');
    const dropdown = wrap.querySelector('.anubis-search-dropdown');
    const form     = wrap.querySelector('[data-anubis-search-bar]');
    if (!input || !dropdown || !form) return;

    let timer       = null;
    let lastQuery   = '';
    let activeIndex = -1;
    let items       = [];

    function getItems() {
      return Array.from(dropdown.querySelectorAll('[data-sd-item]'));
    }

    function setActive(idx) {
      items = getItems();
      items.forEach((el, i) => {
        el.classList.toggle('is-active', i === idx);
        if (i === idx) el.scrollIntoView({ block: 'nearest' });
      });
      activeIndex = idx;
    }

    function open() {
      dropdown.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function close() {
      dropdown.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
    }

    function showLoading() {
      dropdown.innerHTML = '<p class="anubis-sd__status">Buscando…</p>';
      open();
    }

    function showEmpty(q) {
      dropdown.innerHTML = `<p class="anubis-sd__status">Sin resultados para <em>${escapeHtml(q)}</em></p>`;
      open();
    }

    async function search(q) {
      lastQuery = q;
      showLoading();

      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=${MAX_RESULTS}`;
        const res  = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error(res.status);

        const json     = await res.json();
        const products = json?.resources?.results?.products ?? [];
        // Stale check
        if (q !== input.value.trim()) return;

        if (products.length === 0) {
          showEmpty(q);
          return;
        }

        // Expand each product into per-variant cards in parallel, cap total at 10
        const allResultsUrl = `/search?type=product&q=${encodeURIComponent(q)}`;
        const expanded = await Promise.all(products.map(p => expandProduct(p, q)));
        // Stale check again after async work
        if (q !== input.value.trim()) return;
        const cards = expanded.flat().slice(0, 10);
        dropdown.innerHTML = '';
        cards.forEach(card => dropdown.appendChild(card));
        const allLink = document.createElement('a');
        allLink.className = 'anubis-sd__all';
        allLink.href = allResultsUrl;
        allLink.innerHTML = `Ver todos los resultados para <strong>${escapeHtml(q)}</strong>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        dropdown.appendChild(allLink);
        open();
        activeIndex = -1;
      } catch {
        close();
      }
    }

    // Input handler with debounce
    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(timer);

      if (q.length < MIN_CHARS) {
        close();
        return;
      }

      timer = setTimeout(() => search(q), DEBOUNCE_MS);
    });

    // Keyboard navigation
    input.addEventListener('keydown', (e) => {
      if (dropdown.hidden) return;
      items = getItems();

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(Math.min(activeIndex + 1, items.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(Math.max(activeIndex - 1, -1));
        if (activeIndex === -1) input.focus();
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        items[activeIndex]?.click();
      } else if (e.key === 'Escape') {
        close();
        input.blur();
      }
    });

    // Click outside closes
    document.addEventListener('click', (e) => {
      if (!wrap.contains(e.target)) close();
    });

    // Focus reopens if has query
    input.addEventListener('focus', () => {
      const q = input.value.trim();
      if (q.length >= MIN_CHARS && !dropdown.hidden === false) {
        search(q);
      }
    });

    // Prevent form submit closing dropdown prematurely — let native submit go
    form.addEventListener('submit', () => close());
  }

  function initAll() {
    document.querySelectorAll('[data-anubis-search-wrap]').forEach(initBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
